import { MODEL_UI } from '../../copy/model-configuration'
import { t } from '../../copy'
import * as Dialog from '@radix-ui/react-dialog'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Archive, Bot, ChevronDown, Pencil, Plus, Search, ShieldCheck, X } from 'lucide-react'
import { useState } from 'react'

import './skills.css'

import buildPortrait from '../../assets/agent-portraits/lulu-agent-portrait-1.svg'
import designPortrait from '../../assets/agent-portraits/lulu-agent-portrait-2.svg'
import researchPortrait from '../../assets/agent-portraits/lulu-agent-portrait-3.svg'
import { useAccountMembership, useProjects } from '../../app/queries'
import { AITeamNav } from '../ai-team-nav'
import { ModelSourcePicker, type BindableModel } from '../models/model-source-picker'
import { Button } from '../../components/button'
import { Card, CardHint, CardTitle } from '../../components/card'
import { cn } from '../../components/cn'
import { Modal } from '../../components/modal'
import { Page, PageHeader } from '../../components/page'
import { ScreenError, ScreenLoading } from '../../components/state'
import { Tag, type TagTone } from '../../components/tag'
import { Toggle } from '../../components/toggle'
import {
  createLuluAgent,
  disableLuluAgent,
  setLuluSkillEnabled,
  updateLuluAgent,
  useLuluAgentModelCandidates,
  useLuluAgents,
  useLuluSkills,
  type LuluSkill,
  type LuluAgent,
  type LuluAgentInput,
  type LuluAgentModelCandidate,
  type LuluAgentUpdateInput,
} from '../lulu/queries'

type EditorTarget = LuluAgent | 'new' | null
type AgentScopeFilter = 'all' | 'account' | 'project'
type LifecycleFilter = 'all' | 'configured' | 'disabled'
type ProjectOption = { id: string; name: string }
type AgentDraft = {
  displayName: string
  role: string
  instructions: string
  avatarRef: string
  scope: 'account' | 'project'
  projectId: string
  modelMode: 'pinned_profile' | 'system_default'
  systemModuleKey: string
  luluSchedulable: boolean
  selectedModelKey: string
}

function readAgentDraft(): AgentDraft | null {
  try {
    const raw = window.sessionStorage.getItem('npc.agent-draft.v2')
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<AgentDraft>
    if (
      (value.scope !== 'account' && value.scope !== 'project') ||
      (value.modelMode !== 'pinned_profile' && value.modelMode !== 'system_default')
    )
      return null
    return {
      displayName: value.displayName ?? '',
      role: value.role ?? '',
      instructions: value.instructions ?? '',
      avatarRef: value.avatarRef ?? 'agent:build',
      scope: value.scope,
      projectId: value.projectId ?? '',
      modelMode: value.modelMode,
      systemModuleKey: value.systemModuleKey ?? 'lulu_reasoning',
      luluSchedulable: value.luluSchedulable ?? true,
      selectedModelKey: value.selectedModelKey ?? '',
    }
  } catch {
    return null
  }
}

const fieldClass =
  'at-input min-h-11 w-full border border-npc-border bg-npc-panel px-npc-gap-lg text-npc-body text-npc-ink outline-none placeholder:text-npc-ink-softer focus:border-[var(--at-accent-border)] disabled:bg-npc-surface'
const noModelCandidates: readonly LuluAgentModelCandidate[] = []

/** 头像模板 3：角色标记和色板组成的稳定身份，不把模型图标伪装成 Agent。 */
type AvatarTemplate = {
  id: string
  label: string
  hint: string
  src: string
}

const avatarTemplates: ReadonlyArray<AvatarTemplate> = [
  {
    id: 'agent:build',
    label: MODEL_UI.ui_129,
    hint: MODEL_UI.ui_130,
    src: buildPortrait,
  },
  {
    id: 'agent:design',
    label: MODEL_UI.ui_131,
    hint: MODEL_UI.ui_132,
    src: designPortrait,
  },
  {
    id: 'agent:research',
    label: MODEL_UI.ui_133,
    hint: MODEL_UI.ui_132,
    src: researchPortrait,
  },
]

const modelStatusCopy: Readonly<Record<LuluAgent['model_status'], string>> = {
  ok: MODEL_UI.ui_134,
  unverified: MODEL_UI.ui_135,
  unavailable: MODEL_UI.ui_136,
  rate_limited: MODEL_UI.ui_137,
  quota_exhausted: MODEL_UI.ui_138,
  disabled: MODEL_UI.ui_139,
  not_wired: MODEL_UI.ui_140,
  unlisted: MODEL_UI.ui_141,
  unset: MODEL_UI.ui_142,
  unknown: MODEL_UI.ui_143,
}

const systemModuleNames: Readonly<Record<string, string>> = {
  lulu_reasoning: MODEL_UI.ui_144,
  task_planning: MODEL_UI.ui_145,
  development_orchestration: MODEL_UI.ui_146,
  patch_generation: MODEL_UI.ui_147,
  code_review: MODEL_UI.ui_148,
}

function modelTone(status: LuluAgent['model_status']): TagTone {
  switch (status) {
    case 'ok':
      return 'ok'
    case 'unavailable':
    case 'quota_exhausted':
    case 'disabled':
      return 'danger'
    default:
      return 'warn'
  }
}

/** 已配置 = identity saved; badge is coral soft for ALL agents (wave1). Green only on real status elsewhere. */
function lifecycleTone(agent: LuluAgent): TagTone {
  if (agent.disabled) return 'neutral'
  return 'neutral'
}

function candidateKey(candidate: LuluAgentModelCandidate): string {
  return candidate.profile_id
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) return error.message
  return fallback
}

function avatarTemplate(ref: string | null): (typeof avatarTemplates)[number] {
  return avatarTemplates.find((template) => template.id === ref) ?? avatarTemplates[0]!
}

function AgentAvatar({ agent, size = 'md' }: { agent: LuluAgent; size?: 'sm' | 'md' | 'lg' }) {
  return <AvatarPortrait template={avatarTemplate(agent.avatar_ref)} size={size} />
}

function AvatarPortrait({
  template,
  size = 'md',
}: {
  template: AvatarTemplate
  size?: 'sm' | 'md' | 'lg'
}) {
  return (
    <span
      className={cn(
        'relative grid shrink-0 place-items-center overflow-hidden rounded-npc-card border border-npc-border bg-npc-surface-warm text-npc-ink-soft',
        size === 'lg' ? 'h-24 w-20' : size === 'md' ? 'h-16 w-14' : 'h-9 w-8',
      )}
    >
      <span aria-hidden="true" className="text-npc-caption font-semibold">
        {template.label.slice(0, 1)}
      </span>
      <img
        aria-hidden="true"
        data-testid="agent-avatar-portrait"
        alt=""
        src={template.src}
        className="absolute inset-0 h-full w-full object-cover object-top"
        onError={(event) => {
          event.currentTarget.hidden = true
        }}
      />
    </span>
  )
}

function agentScopeLabel(agent: LuluAgent, projectName?: string): string {
  return agent.scope === 'project'
    ? projectName
      ? t('model_configuration.project_scope_named', { project: projectName })
      : t('model_configuration.project_scope')
    : MODEL_UI.ui_149
}

/** 长期 Agent 配置库：运行态只属于 Lulu 工作板。 */
export function SkillsScreen() {
  const [scopeFilter, setScopeFilter] = useState<AgentScopeFilter>('all')
  const [projectFilter, setProjectFilter] = useState('')
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleFilter>('all')
  const [search, setSearch] = useState('')
  const [editor, setEditor] = useState<EditorTarget>(() =>
    new URLSearchParams(window.location.search).get('restore_agent') === '1' ? 'new' : null,
  )
  const [disableTarget, setDisableTarget] = useState<LuluAgent | null>(null)
  const agents = useLuluAgents('all', undefined, true)
  const projects = useProjects()
  const membership = useAccountMembership()
  const queryClient = useQueryClient()
  const invalidateAgents = async () => queryClient.invalidateQueries({ queryKey: ['lulu-agents'] })
  const create = useMutation({ mutationFn: createLuluAgent, onSuccess: invalidateAgents })
  const update = useMutation({
    mutationFn: ({ agentId, input }: { agentId: string; input: LuluAgentUpdateInput }) =>
      updateLuluAgent(agentId, input),
    onSuccess: invalidateAgents,
  })
  const disable = useMutation({ mutationFn: disableLuluAgent, onSuccess: invalidateAgents })

  if (agents.isPending || membership.isPending)
    return (
      <ScreenLoading
        shape="cards"
        cols={2}
        width="workspace"
        className="ai-team-theme min-h-full bg-m-bg0 text-npc-ink"
      />
    )
  if (agents.error) return <ScreenError error={agents.error} />

  const allAgents = agents.data?.agents ?? []
  const capabilities = membership.data?.capabilities ?? []
  const canManageAccountAgents = capabilities.includes('vendor.manage')
  const canManageProjectAgents = capabilities.includes('project.manage')
  const canAddAgent = canManageAccountAgents || canManageProjectAgents
  const projectOptions: readonly ProjectOption[] = projects.data?.items ?? []
  const projectNames = new Map(projectOptions.map((project) => [project.id, project.name]))
  const normalizedSearch = search.trim().toLocaleLowerCase()
  const visibleAgents = allAgents.filter((agent) => {
    if (scopeFilter !== 'all' && agent.scope !== scopeFilter) return false
    if (scopeFilter === 'project' && projectFilter && agent.project_id !== projectFilter)
      return false
    if (lifecycleFilter === 'configured' && agent.disabled) return false
    if (lifecycleFilter === 'disabled' && !agent.disabled) return false
    if (!normalizedSearch) return true
    const projectName = agent.project_id ? (projectNames.get(agent.project_id) ?? '') : ''
    return [agent.display_name, agent.role ?? '', projectName]
      .join('\n')
      .toLocaleLowerCase()
      .includes(normalizedSearch)
  })
  const configuredAgents = visibleAgents.filter((agent) => !agent.disabled)
  const disabledAgents = visibleAgents.filter((agent) => agent.disabled)
  const isFiltered = Boolean(normalizedSearch) || scopeFilter !== 'all' || lifecycleFilter !== 'all'

  return (
    <Page className="ai-team-theme min-h-full bg-m-bg0 text-npc-ink" width="workspace">
      <AITeamNav current="/skills" />
      <PageHeader
        title={MODEL_UI.ui_150}
        subtitle={MODEL_UI.ui_151}
        actions={
          canAddAgent ? (
            <Button variant="primary" className="at-pill" onClick={() => setEditor('new')}>
              <Plus size={16} aria-hidden="true" />
              {MODEL_UI.ui_152}
            </Button>
          ) : null
        }
      />

      <section className="mt-npc-modal" aria-label={MODEL_UI.ui_153}>
        <div className="grid gap-npc-gap-lg rounded-npc-card border border-npc-border-soft bg-npc-surface p-npc-card sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <label className="grid gap-npc-gap-xs">
            <span className="text-npc-caption font-semibold text-npc-ink-medium">
              {MODEL_UI.ui_154}
            </span>
            <span className="relative block">
              <Search
                className="pointer-events-none absolute left-npc-gap-lg top-1/2 -translate-y-1/2 text-npc-ink-soft"
                size={16}
                aria-hidden="true"
              />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className={cn(fieldClass, 'pl-10')}
                placeholder={MODEL_UI.ui_155}
                aria-label={MODEL_UI.ui_156}
              />
            </span>
          </label>
          <FilterButtons
            label={MODEL_UI.ui_157}
            active={scopeFilter}
            onChange={setScopeFilter}
            choices={[
              ['all', MODEL_UI.ui_158],
              ['account', MODEL_UI.ui_149],
              ['project', MODEL_UI.ui_159],
            ]}
          />
        </div>
        <div className="mt-npc-gap-lg flex flex-wrap items-end gap-npc-gap-lg">
          {scopeFilter === 'project' ? (
            <label className="grid min-w-[220px] gap-npc-gap-xs">
              <span className="text-npc-caption font-semibold text-npc-ink-medium">
                {MODEL_UI.ui_160}
              </span>
              <select
                value={projectFilter}
                onChange={(event) => setProjectFilter(event.target.value)}
                className={fieldClass}
                aria-label={MODEL_UI.ui_160}
              >
                <option value="">{MODEL_UI.ui_161}</option>
                {projectOptions.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <FilterButtons
            label={MODEL_UI.ui_162}
            active={lifecycleFilter}
            onChange={setLifecycleFilter}
            choices={[
              ['all', MODEL_UI.ui_158],
              ['configured', MODEL_UI.ui_163],
              ['disabled', MODEL_UI.ui_164],
            ]}
          />
        </div>
      </section>

      {!canAddAgent ? (
        <div className="mt-npc-grid-lg border-y border-npc-border bg-npc-surface px-npc-card py-npc-grid-sm text-npc-caption text-npc-ink-medium">
          {MODEL_UI.ui_165}
        </div>
      ) : null}
      {allAgents.length === 0 ? (
        <section className="mt-npc-modal">
          <EmptyConfiguredAgents canAdd={canAddAgent} onAdd={() => setEditor('new')} />
        </section>
      ) : null}
      {allAgents.length > 0 && visibleAgents.length === 0 ? (
        <section className="mt-npc-modal">
          <EmptySearch
            canAdd={canAddAgent}
            onClear={() => {
              setSearch('')
              setScopeFilter('all')
              setProjectFilter('')
              setLifecycleFilter('all')
            }}
            onAdd={() => setEditor('new')}
          />
        </section>
      ) : null}
      {configuredAgents.length > 0 ? (
        <AgentSection
          title={MODEL_UI.ui_166}
          hint={t('model_configuration.agent_count', { count: configuredAgents.length })}
          agents={configuredAgents}
          projectNames={projectNames}
          canManageAccount={canManageAccountAgents}
          canManageProject={canManageProjectAgents}
          onEdit={setEditor}
          onDisable={setDisableTarget}
        />
      ) : null}
      {disabledAgents.length > 0 ? (
        <AgentSection
          title={MODEL_UI.ui_164}
          hint={MODEL_UI.ui_167}
          agents={disabledAgents}
          projectNames={projectNames}
        />
      ) : null}

      {editor ? (
        <AgentEditor
          key={editor === 'new' ? 'new' : editor.id}
          agent={editor === 'new' ? null : editor}
          projects={projectOptions}
          canManageAccount={canManageAccountAgents}
          canManageProject={canManageProjectAgents}
          saving={create.isPending || update.isPending}
          error={create.error ?? update.error}
          onOpenChange={(open) => !open && setEditor(null)}
          onSave={async (input, preserveGeneratedName) => {
            if (editor === 'new') await create.mutateAsync(input)
            else {
              const updateInput: LuluAgentUpdateInput = {
                avatarRef: input.avatarRef,
                role: input.role,
                instructions: input.instructions,
                modelPolicy: input.modelPolicy,
                luluSchedulable: input.luluSchedulable,
                ...(preserveGeneratedName ? {} : { displayName: input.displayName }),
              }
              await update.mutateAsync({ agentId: editor.id, input: updateInput })
            }
            window.sessionStorage.removeItem('npc.agent-draft.v2')
            setEditor(null)
          }}
        />
      ) : null}
      {disableTarget ? (
        <DisableAgentDialog
          agent={disableTarget}
          busy={disable.isPending}
          error={disable.error}
          onOpenChange={(open) => !open && setDisableTarget(null)}
          onConfirm={async () => {
            await disable.mutateAsync(disableTarget.id)
            setDisableTarget(null)
          }}
        />
      ) : null}
      {isFiltered ? (
        <p className="mt-npc-card text-npc-meta text-npc-ink-soft">{MODEL_UI.ui_168}</p>
      ) : null}

      {/* Lulu 的技能：注册表的投影 + 账号级开关。定义在服务端，这里只有开关。 */}
      <LuluSkillsSection canManage={canManageAccountAgents} />
    </Page>
  )
}

function FilterButtons<T extends string>({
  label,
  active,
  onChange,
  choices,
}: {
  label: string
  active: T
  onChange: (next: T) => void
  choices: readonly (readonly [T, string])[]
}) {
  return (
    <div className="grid gap-npc-gap-xs">
      <span className="text-npc-caption font-semibold text-npc-ink-medium">{label}</span>
      <div
        className="flex flex-wrap gap-npc-gap-xs"
        role="group"
        aria-label={t('model_configuration.filter_aria', { label })}
      >
        {choices.map(([value, label]) => (
          <Button
            key={value}
            size="sm"
            variant="secondary"
            className={cn('at-pill', active === value && 'at-seg-active')}
            onClick={() => onChange(value)}
          >
            {label}
          </Button>
        ))}
      </div>
    </div>
  )
}

function EmptyConfiguredAgents({ canAdd, onAdd }: { canAdd: boolean; onAdd: () => void }) {
  return (
    <div className="border-y border-npc-border-soft py-npc-section">
      <div className="flex items-start gap-npc-gap-lg max-[420px]:gap-npc-gap-sm">
        <span className="inline-flex size-11 items-center justify-center rounded-npc-card bg-npc-surface text-npc-ink-soft">
          <Bot size={21} aria-hidden="true" />
        </span>
        <div>
          <CardTitle>{MODEL_UI.ui_169}</CardTitle>
          <CardHint className="mt-npc-gap-xs">{MODEL_UI.ui_170}</CardHint>
          {canAdd ? (
            <Button className="mt-npc-card" size="sm" variant="primary" onClick={onAdd}>
              <Plus size={15} aria-hidden="true" />
              {MODEL_UI.ui_152}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function EmptySearch({
  canAdd,
  onClear,
  onAdd,
}: {
  canAdd: boolean
  onClear: () => void
  onAdd: () => void
}) {
  return (
    <div className="border-y border-npc-border-soft py-npc-section">
      <CardTitle>{MODEL_UI.ui_171}</CardTitle>
      <CardHint className="mt-npc-gap-xs">{MODEL_UI.ui_172}</CardHint>
      <div className="mt-npc-card flex flex-wrap gap-npc-gap-sm">
        <Button size="sm" onClick={onClear}>
          {MODEL_UI.ui_173}
        </Button>
        {canAdd ? (
          <Button size="sm" variant="primary" onClick={onAdd}>
            {MODEL_UI.ui_152}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

function AgentSection({
  title,
  hint,
  agents,
  projectNames,
  canManageAccount,
  canManageProject,
  onEdit,
  onDisable,
}: {
  title: string
  hint: string
  agents: readonly LuluAgent[]
  projectNames: ReadonlyMap<string, string>
  canManageAccount?: boolean
  canManageProject?: boolean
  onEdit?: (agent: LuluAgent) => void
  onDisable?: (agent: LuluAgent) => void
}) {
  const canManageAgent = (agent: LuluAgent) =>
    agent.scope === 'account' ? Boolean(canManageAccount) : Boolean(canManageProject)
  return (
    <section className="mt-npc-modal" aria-labelledby={`${title}-agents-heading`}>
      <div className="mb-npc-grid-sm flex flex-wrap items-center justify-between gap-npc-gap">
        <h2 id={`${title}-agents-heading`} className="m-0 text-npc-section font-semibold">
          {title}
        </h2>
        <span className="text-npc-caption text-npc-ink-soft">{hint}</span>
      </div>
      <div className="agent-library-grid grid gap-npc-grid-sm">
        {agents.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            projectName={agent.project_id ? projectNames.get(agent.project_id) : undefined}
            onEdit={onEdit && canManageAgent(agent) ? () => onEdit(agent) : undefined}
            onDisable={onDisable && canManageAgent(agent) ? () => onDisable(agent) : undefined}
          />
        ))}
      </div>
    </section>
  )
}

function AgentCard({
  agent,
  projectName,
  onEdit,
  onDisable,
}: {
  agent: LuluAgent
  projectName?: string
  onEdit?: () => void
  onDisable?: () => void
}) {
  const modelLabel = agent.system_module_key
    ? t('model_configuration.system_default_model', {
        module: systemModuleNames[agent.system_module_key] ?? agent.system_module_key,
      })
    : agent.model_profile_id
      ? t('model_configuration.explicit_model', {
          model: agent.model_display_name || agent.model_id || MODEL_UI.ui_174,
        })
      : MODEL_UI.ui_142
  return (
    <Card
      className={cn('min-w-0', agent.disabled && 'opacity-65')}
      data-testid={`agent-${agent.id}`}
    >
      <div className="flex items-start gap-npc-gap-lg">
        <AgentAvatar agent={agent} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-npc-gap-sm">
            <div className="min-w-0">
              <CardTitle className="truncate">{agent.display_name}</CardTitle>
              <div className="mt-npc-gap-xs flex flex-wrap items-center gap-npc-gap-xs">
                <span className="text-npc-caption text-npc-ink-soft">
                  {agent.role || MODEL_UI.ui_175}
                </span>
                {agent.display_name_source === 'system' ? <Tag>{MODEL_UI.ui_176}</Tag> : null}
              </div>
            </div>
            <Tag
              tone={lifecycleTone(agent)}
              className={agent.disabled ? undefined : 'at-status-cfg'}
            >
              {agent.disabled ? MODEL_UI.ui_164 : MODEL_UI.ui_163}
            </Tag>
          </div>
          <div className="mt-npc-card grid gap-npc-gap-xs text-npc-caption text-npc-ink-medium">
            <div className="flex min-w-0 items-center gap-npc-gap-xs">
              <ShieldCheck size={15} aria-hidden="true" className="shrink-0 text-npc-ink-soft" />
              <span className="truncate" title={modelLabel}>
                {modelLabel}
              </span>
              {!agent.system_module_key && agent.model_connection ? (
                <Tag tone="neutral">{agent.model_connection}</Tag>
              ) : null}
            </div>
            <span className="text-npc-meta text-npc-ink-soft">
              {agentScopeLabel(agent, projectName)} ·{' '}
              {agent.lulu_schedulable ? MODEL_UI.ui_177 : MODEL_UI.ui_178}
            </span>
          </div>
          {!agent.disabled && agent.model_status !== 'ok' && agent.model_status !== 'unset' ? (
            <CardHint
              className={cn(
                'mt-npc-gap-xs',
                modelTone(agent.model_status) === 'danger' && 'text-npc-red-ink',
              )}
            >
              {modelStatusCopy[agent.model_status]}
            </CardHint>
          ) : null}
          {!agent.disabled && onEdit && onDisable ? (
            <div className="mt-npc-card flex flex-wrap gap-npc-gap-sm">
              <Button
                size="sm"
                onClick={onEdit}
                aria-label={t('model_configuration.action_target', {
                  action: MODEL_UI.ui_032,
                  name: agent.display_name,
                })}
              >
                <Pencil size={14} aria-hidden="true" />
                {MODEL_UI.ui_032}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onDisable}
                aria-label={t('model_configuration.action_target', {
                  action: MODEL_UI.ui_179,
                  name: agent.display_name,
                })}
              >
                <Archive size={14} aria-hidden="true" />
                {MODEL_UI.ui_179}
              </Button>
            </div>
          ) : agent.disabled ? (
            <CardHint className="mt-npc-card">{MODEL_UI.ui_180}</CardHint>
          ) : (
            <CardHint className="mt-npc-card">{MODEL_UI.ui_181}</CardHint>
          )}
        </div>
      </div>
    </Card>
  )
}

function AgentEditor({
  agent,
  projects,
  canManageAccount,
  canManageProject,
  saving,
  error,
  onOpenChange,
  onSave,
}: {
  agent: LuluAgent | null
  projects: readonly ProjectOption[]
  canManageAccount: boolean
  canManageProject: boolean
  saving: boolean
  error: unknown
  onOpenChange: (open: boolean) => void
  onSave: (input: LuluAgentInput, preserveGeneratedName: boolean) => Promise<void>
}) {
  const draft = agent ? null : readAgentDraft()
  const [displayName, setDisplayName] = useState(agent?.display_name ?? draft?.displayName ?? '')
  const [role, setRole] = useState(agent?.role ?? draft?.role ?? '')
  const [instructions, setInstructions] = useState(agent?.instructions ?? draft?.instructions ?? '')
  const [avatarRef, setAvatarRef] = useState(agent?.avatar_ref ?? draft?.avatarRef ?? 'agent:build')
  const [scope, setScope] = useState<'account' | 'project'>(
    agent?.scope ?? draft?.scope ?? (canManageAccount ? 'account' : 'project'),
  )
  const [projectId, setProjectId] = useState(agent?.project_id ?? draft?.projectId ?? '')
  const [modelMode, setModelMode] = useState<'pinned_profile' | 'system_default'>(
    agent?.system_module_key ? 'system_default' : (draft?.modelMode ?? 'pinned_profile'),
  )
  const [systemModuleKey, setSystemModuleKey] = useState(
    agent?.system_module_key ?? draft?.systemModuleKey ?? 'lulu_reasoning',
  )
  const [luluSchedulable, setLuluSchedulable] = useState(
    agent?.lulu_schedulable ?? draft?.luluSchedulable ?? true,
  )
  const [identityOpen, setIdentityOpen] = useState(!agent)
  const [modelPickerOpen, setModelPickerOpen] = useState(false)
  const models = useLuluAgentModelCandidates(
    scope,
    scope === 'project' ? projectId || undefined : undefined,
  )
  const candidates = models.data?.models ?? noModelCandidates
  const [materializedCandidate, setMaterializedCandidate] =
    useState<LuluAgentModelCandidate | null>(null)
  const selectableCandidates =
    materializedCandidate &&
    !candidates.some((candidate) => candidate.profile_id === materializedCandidate.profile_id)
      ? [...candidates, materializedCandidate]
      : candidates
  const [selectedModelKey, setSelectedModelKey] = useState(
    agent?.model_profile_id ?? draft?.selectedModelKey ?? '',
  )
  const selected = selectableCandidates.find(
    (candidate) => candidateKey(candidate) === selectedModelKey,
  )
  const canManageScope = scope === 'account' ? canManageAccount : canManageProject
  const canSave =
    canManageScope &&
    (modelMode === 'system_default' || Boolean(selected?.selectable)) &&
    !saving &&
    (modelMode === 'system_default' || !models.isPending) &&
    (scope !== 'project' || Boolean(projectId))

  const chooseScope = (nextScope: 'account' | 'project') => {
    if (agent) return
    setScope(nextScope)
    setSelectedModelKey('')
    setModelPickerOpen(false)
  }
  const chooseProject = (nextProjectId: string) => {
    setProjectId(nextProjectId)
    setSelectedModelKey('')
    setModelPickerOpen(false)
  }
  const manageModels = (providerId?: string) => {
    window.sessionStorage.setItem(
      'npc.agent-draft.v2',
      JSON.stringify({
        displayName,
        role,
        instructions,
        avatarRef,
        scope,
        projectId,
        modelMode,
        systemModuleKey,
        luluSchedulable,
        selectedModelKey,
      } satisfies AgentDraft),
    )
    const params = new URLSearchParams({ return_to: '/skills?restore_agent=1' })
    if (providerId) params.set('add_provider', providerId)
    const target = `/models?${params.toString()}`
    window.history.pushState({}, '', target)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }
  const submit = async () => {
    if (
      (modelMode === 'pinned_profile' && !selected?.selectable) ||
      (scope === 'project' && !projectId)
    )
      return
    const normalizedDisplayName = displayName.trim()
    await onSave(
      {
        displayName: normalizedDisplayName,
        avatarRef,
        role: role.trim(),
        instructions: instructions.trim(),
        scope,
        ...(scope === 'project' ? { projectId } : {}),
        modelPolicy:
          modelMode === 'system_default'
            ? { mode: 'system_default', module_key: systemModuleKey }
            : { mode: 'pinned_profile', profile_id: selected!.profile_id },
        luluSchedulable,
      },
      Boolean(
        agent &&
        agent.display_name_source === 'system' &&
        normalizedDisplayName === agent.display_name,
      ),
    )
  }

  const body = (
    <div className="grid gap-npc-card">
      {agent ? (
        <section className="grid justify-items-center gap-npc-grid-sm border-b border-npc-border pb-npc-card text-center">
          <AgentAvatar agent={{ ...agent, avatar_ref: avatarRef }} size="lg" />
          <div>
            <h2 className="m-0 text-npc-section font-semibold text-npc-ink">
              {displayName || agent.display_name}
            </h2>
            <p className="mb-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
              {role || agent.role}
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={() => setIdentityOpen((open) => !open)}>
            <Pencil size={14} aria-hidden="true" />
            {identityOpen
              ? t('model_configuration.finish_agent_information')
              : t('model_configuration.modify_agent_information')}
          </Button>
        </section>
      ) : null}

      <div className={cn('grid gap-npc-card', agent && !identityOpen && 'hidden')}>
        <div className="grid gap-npc-grid-sm md:grid-cols-2">
          <label className="grid gap-npc-gap-xs">
            <span className="text-npc-caption font-semibold text-npc-ink-medium">
              {MODEL_UI.ui_182}
            </span>
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              className={fieldClass}
              placeholder={MODEL_UI.ui_183}
              aria-label={MODEL_UI.ui_184}
            />
          </label>
          <label className="grid gap-npc-gap-xs">
            <span className="text-npc-caption font-semibold text-npc-ink-medium">
              {MODEL_UI.ui_185}
            </span>
            <input
              value={role}
              onChange={(event) => setRole(event.target.value)}
              className={fieldClass}
              placeholder={MODEL_UI.ui_186}
              aria-label={MODEL_UI.ui_187}
            />
          </label>
        </div>
        <fieldset className="m-0 border-0 p-0">
          <legend className="text-npc-caption font-semibold text-npc-ink-medium">
            {MODEL_UI.ui_188}
          </legend>
          <p className="mb-0 mt-npc-gap-xs text-npc-meta text-npc-ink-soft">{MODEL_UI.ui_189}</p>
          <div className="mt-npc-gap-sm grid grid-cols-3 gap-npc-gap-sm max-[420px]:grid-cols-1">
            {avatarTemplates.map((template) => {
              const selectedAvatar = avatarRef === template.id
              return (
                <button
                  key={template.id}
                  type="button"
                  aria-label={t('model_configuration.avatar_select_aria', {
                    avatar: template.label,
                  })}
                  aria-pressed={selectedAvatar}
                  onClick={() => setAvatarRef(template.id)}
                  className={cn(
                    'flex min-h-28 flex-col items-center gap-npc-gap-sm rounded-npc-card border p-npc-card-sm text-center',
                    selectedAvatar
                      ? 'at-seg-active'
                      : 'border-npc-border bg-npc-panel hover:border-npc-border-hover',
                  )}
                >
                  <AvatarPortrait template={template} />
                  <span>
                    <span className="block text-npc-caption font-semibold text-npc-ink">
                      {template.label}
                    </span>
                    <span className="block text-npc-meta text-npc-ink-soft">{template.hint}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </fieldset>
      </div>

      <div className="text-npc-caption font-semibold text-npc-ink-soft">
        {t('model_configuration.agent_capabilities_resources')}
      </div>
      <fieldset className="m-0 border-0 p-0">
        <legend className="text-npc-caption font-semibold text-npc-ink-medium">
          {MODEL_UI.ui_190}
        </legend>
        <div className="mt-npc-gap-sm grid grid-cols-2 gap-npc-gap-sm max-[600px]:grid-cols-1">
          <button
            type="button"
            aria-pressed={modelMode === 'pinned_profile'}
            onClick={() => setModelMode('pinned_profile')}
            className={cn(
              'rounded-npc-card border p-npc-card-sm text-left',
              modelMode === 'pinned_profile'
                ? 'at-seg-active'
                : 'border-npc-border bg-npc-panel',
            )}
          >
            <span className="block text-npc-caption font-semibold">{MODEL_UI.ui_191}</span>
            <span className="mt-npc-gap-xs block text-npc-meta text-npc-ink-soft">
              {MODEL_UI.ui_192}
            </span>
          </button>
          <button
            type="button"
            aria-pressed={modelMode === 'system_default'}
            onClick={() => setModelMode('system_default')}
            className={cn(
              'rounded-npc-card border p-npc-card-sm text-left',
              modelMode === 'system_default'
                ? 'at-seg-active'
                : 'border-npc-border bg-npc-panel',
            )}
          >
            <span className="block text-npc-caption font-semibold">{MODEL_UI.ui_193}</span>
            <span className="mt-npc-gap-xs block text-npc-meta text-npc-ink-soft">
              {MODEL_UI.ui_194}
            </span>
          </button>
        </div>
      </fieldset>
      {modelMode === 'pinned_profile' ? (
        <ModelPicker
          candidates={selectableCandidates}
          selectedModelKey={selectedModelKey}
          onSelect={(candidate) => {
            setMaterializedCandidate(candidate)
            setSelectedModelKey(candidate.profile_id)
            setModelPickerOpen(false)
          }}
          open={modelPickerOpen}
          onOpenChange={setModelPickerOpen}
          loading={models.isPending}
          error={models.error}
          scope={scope}
          projectId={scope === 'project' ? projectId || undefined : undefined}
          projectName={projects.find((project) => project.id === projectId)?.name}
          onManageModels={manageModels}
        />
      ) : (
        <label className="grid gap-npc-gap-xs">
          <span className="text-npc-caption font-semibold text-npc-ink-medium">
            {MODEL_UI.ui_111}
          </span>
          <select
            value={systemModuleKey}
            onChange={(event) => setSystemModuleKey(event.target.value)}
            className={fieldClass}
          >
            <option value="lulu_reasoning">{MODEL_UI.ui_144}</option>
            <option value="task_planning">{MODEL_UI.ui_145}</option>
            <option value="development_orchestration">{MODEL_UI.ui_146}</option>
            <option value="patch_generation">{MODEL_UI.ui_147}</option>
            <option value="code_review">{MODEL_UI.ui_148}</option>
          </select>
          <span className="text-npc-meta text-npc-amber-ink">{MODEL_UI.ui_195}</span>
        </label>
      )}
      <fieldset className="m-0 border-0 p-0">
        <legend className="text-npc-caption font-semibold text-npc-ink-medium">
          {MODEL_UI.ui_196}
        </legend>
        {agent ? (
          <p className="mb-0 mt-npc-gap-xs text-npc-caption text-npc-ink-medium">
            {agentScopeLabel(
              agent,
              projects.find((project) => project.id === agent.project_id)?.name,
            )}
            {MODEL_UI.ui_197}
          </p>
        ) : (
          <>
            <div
              className="mt-npc-gap-sm flex flex-wrap gap-npc-gap-sm"
              role="group"
              aria-label={MODEL_UI.ui_198}
            >
              {canManageAccount ? (
                <Button
                  size="sm"
                  variant={scope === 'account' ? 'primary' : 'secondary'}
                  onClick={() => chooseScope('account')}
                >
                  {MODEL_UI.ui_149}
                </Button>
              ) : null}
              {canManageProject ? (
                <Button
                  size="sm"
                  variant={scope === 'project' ? 'primary' : 'secondary'}
                  onClick={() => chooseScope('project')}
                >
                  {MODEL_UI.ui_159}
                </Button>
              ) : null}
            </div>
            {scope === 'project' ? (
              <label className="mt-npc-gap-sm grid gap-npc-gap-xs">
                <span className="text-npc-caption text-npc-ink-medium">{MODEL_UI.ui_199}</span>
                <select
                  value={projectId}
                  onChange={(event) => chooseProject(event.target.value)}
                  className={fieldClass}
                  aria-label={MODEL_UI.ui_200}
                >
                  <option value="">{MODEL_UI.ui_201}</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </>
        )}
      </fieldset>
      <label className="grid gap-npc-gap-xs">
        <span className="text-npc-caption font-semibold text-npc-ink-medium">
          {MODEL_UI.ui_202}
        </span>
        <textarea
          value={instructions}
          onChange={(event) => setInstructions(event.target.value)}
          className={cn(fieldClass, 'min-h-[96px] resize-y py-npc-gap-lg')}
          rows={3}
          placeholder={MODEL_UI.ui_203}
          aria-label={MODEL_UI.ui_204}
        />
      </label>
      <div className="flex items-start justify-between gap-npc-card rounded-npc-card border border-npc-border bg-npc-surface p-npc-card-sm max-[420px]:flex-col">
        <div>
          <div className="text-npc-caption font-semibold text-npc-ink">{MODEL_UI.ui_177}</div>
          <p className="mb-0 mt-npc-gap-xs text-npc-meta text-npc-ink-soft">{MODEL_UI.ui_205}</p>
        </div>
        <Toggle
          checked={luluSchedulable}
          onCheckedChange={setLuluSchedulable}
          label={luluSchedulable ? MODEL_UI.ui_206 : MODEL_UI.ui_178}
        />
      </div>
      {error ? (
        <div role="alert" className="text-npc-caption text-npc-red-ink">
          {errorMessage(error, MODEL_UI.ui_207)}
          {MODEL_UI.ui_208}
        </div>
      ) : null}
    </div>
  )
  const footer = (
    <>
      <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
        {MODEL_UI.ui_042}
      </Button>
      <Button variant="primary" disabled={!canSave} onClick={() => void submit()}>
        {saving ? MODEL_UI.ui_069 : agent ? MODEL_UI.ui_209 : MODEL_UI.ui_210}
      </Button>
    </>
  )

  const title = agent ? t('model_configuration.agent_settings_title') : MODEL_UI.ui_152
  const description = agent ? MODEL_UI.ui_211 : MODEL_UI.ui_212
  return (
    <Dialog.Root open onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-npc-overlay" />
        <Dialog.Content className="ai-team-theme fixed inset-y-0 right-0 z-50 flex w-[min(620px,100vw)] flex-col border-l border-npc-border bg-npc-panel shadow-npc-popover max-[600px]:w-screen">
          <header className="flex shrink-0 items-start justify-between gap-npc-gap border-b border-npc-border-soft p-npc-card">
            <div className="min-w-0">
              <Dialog.Title className="m-0 truncate text-npc-section font-semibold text-npc-ink">
                {title}
              </Dialog.Title>
              <Dialog.Description className="mb-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
                {description}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button size="sm" variant="ghost" aria-label={MODEL_UI.ui_213}>
                <X size={16} aria-hidden="true" />
              </Button>
            </Dialog.Close>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto p-npc-card max-[600px]:p-npc-grid-sm">
            {body}
          </div>
          <footer className="flex shrink-0 justify-end gap-npc-gap-sm border-t border-npc-border-soft p-npc-card max-[420px]:grid max-[420px]:grid-cols-2 max-[420px]:p-npc-grid-sm">
            {footer}
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function ModelPicker({
  candidates,
  selectedModelKey,
  onSelect,
  open,
  onOpenChange,
  loading,
  error,
  scope,
  projectId,
  projectName,
  onManageModels,
}: {
  candidates: readonly LuluAgentModelCandidate[]
  selectedModelKey: string
  onSelect: (candidate: LuluAgentModelCandidate) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  loading: boolean
  error: unknown
  scope: 'account' | 'project'
  projectId?: string
  projectName?: string
  onManageModels: (providerId?: string) => void
}) {
  const selected = candidates.find((candidate) => candidateKey(candidate) === selectedModelKey)
  const options: BindableModel[] = candidates.map((candidate) => ({
    profileId: candidate.profile_id,
    displayName: candidate.display_name,
    modelId: candidate.model_id,
    provider: candidate.provider,
    connectionId: '',
    connectionName: candidate.connection_name,
  }))
  return (
    <fieldset className="m-0 border-0 p-0" aria-describedby="agent-model-hint">
      <legend className="text-npc-caption font-semibold text-npc-ink-medium">
        {MODEL_UI.ui_214}
      </legend>
      <p id="agent-model-hint" className="mb-0 mt-npc-gap-xs text-npc-meta text-npc-ink-soft">
        {MODEL_UI.ui_215}
      </p>
      <div className="relative mt-npc-gap-sm">
        <button
          type="button"
          aria-label={MODEL_UI.ui_216}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => onOpenChange(!open)}
          className={cn(fieldClass, 'flex items-center justify-between gap-npc-gap text-left')}
        >
          <span className="min-w-0">
            <span className="block truncate text-npc-caption font-semibold">
              {selected ? selected.display_name : MODEL_UI.ui_216}
            </span>
            <span className="block truncate text-npc-meta text-npc-ink-soft">
              {selected
                ? `${selected.provider}/${selected.model_id} · ${selected.connection_name}`
                : MODEL_UI.ui_217}
            </span>
          </span>
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={cn('shrink-0 transition-transform', open && 'rotate-180')}
          />
        </button>
        {open ? (
          <div
            className="absolute inset-x-0 z-20 mt-npc-gap-xs overflow-hidden rounded-npc-card border border-npc-border bg-npc-panel shadow-npc-popover max-[600px]:static max-[600px]:max-h-none"
            role="listbox"
            aria-label={MODEL_UI.ui_218}
          >
            <div className="p-npc-gap-sm">
              {loading ? (
                <div className="p-npc-gap-sm text-npc-caption text-npc-ink-soft">
                  {MODEL_UI.ui_221}
                </div>
              ) : null}
              {error ? (
                <div className="p-npc-gap-sm text-npc-caption text-npc-amber-ink">
                  {errorMessage(error, MODEL_UI.ui_222)}
                  {MODEL_UI.ui_223}
                </div>
              ) : null}
              <ModelSourcePicker
                options={options}
                value={selectedModelKey}
                scope={scope}
                projectId={projectId}
                onSelect={(_profileId, option) =>
                  onSelect(
                    candidates.find((candidate) => candidate.profile_id === option.profileId) ?? {
                      profile_id: option.profileId,
                      display_name: option.displayName,
                      connection_name: option.connectionName,
                      scope,
                      provider: option.provider,
                      model_id: option.modelId,
                      origin: 'model_library',
                      purpose: 'agent',
                      selectable: true,
                      status: 'ok',
                    },
                  )
                }
                onManageModels={onManageModels}
              />
              {projectName ? (
                <p className="mb-0 mt-npc-gap-xs text-npc-meta text-npc-ink-soft">
                  {t('model_configuration.project_scope_named', { project: projectName })}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </fieldset>
  )
}

function DisableAgentDialog({
  agent,
  busy,
  error,
  onOpenChange,
  onConfirm,
}: {
  agent: LuluAgent
  busy: boolean
  error: unknown
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void>
}) {
  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title={t('model_configuration.action_target', {
        action: MODEL_UI.ui_179,
        name: agent.display_name,
      })}
      description={MODEL_UI.ui_227}
      className="ai-team-theme"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            {MODEL_UI.ui_042}
          </Button>
          <Button variant="danger" disabled={busy} onClick={() => void onConfirm()}>
            {busy ? MODEL_UI.ui_228 : MODEL_UI.ui_229}
          </Button>
        </>
      }
    >
      <p className="m-0 text-npc-caption text-npc-ink-medium">{MODEL_UI.ui_230}</p>
      {error ? (
        <p role="alert" className="mb-0 mt-npc-card text-npc-caption text-npc-red-ink">
          {MODEL_UI.ui_231}
        </p>
      ) : null}
    </Modal>
  )
}


/** 多久之前，说人话：刚刚 / N 分钟前 / N 小时前 / N 天前。 */
function agoText(iso: string, nowMs: number): string {
  const ms = nowMs - Date.parse(iso)
  if (!Number.isFinite(ms) || ms < 60_000) return t('lulu_workbench.skills.ago_now')
  const min = Math.floor(ms / 60_000)
  if (min < 60) return t('lulu_workbench.skills.ago_minutes', { n: String(min) })
  const h = Math.floor(min / 60)
  if (h < 24) return t('lulu_workbench.skills.ago_hours', { n: String(h) })
  return t('lulu_workbench.skills.ago_days', { n: String(Math.floor(h / 24)) })
}

/**
 * 「Lulu 的技能」那一段：每张卡是注册表里一个技能的投影——怎么触发、上次跑了没、开没开。
 * 开关是账号级的（需求稿 §8.2）；没权限的人看得见、拧不动。
 */
export function LuluSkillsSection({ canManage }: { canManage: boolean }) {
  const skills = useLuluSkills()
  const queryClient = useQueryClient()
  const [failedKey, setFailedKey] = useState<string | null>(null)
  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) => setLuluSkillEnabled(key, enabled),
    onMutate: () => setFailedKey(null),
    onError: (_err, vars) => setFailedKey(vars.key),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['lulu-skills'] }),
  })
  // 这条边没挂（没有回话循环）时服务端 404：那一段整个不显示，不显示一个空壳。
  if (skills.isPending || skills.error) return null
  const list: readonly LuluSkill[] = skills.data?.skills ?? []
  const now = Date.now()
  return (
    <section className="mt-npc-modal" aria-label={t('lulu_workbench.skills.section')} data-testid="lulu-skills-section">
      <h2 className="m-0 text-npc-section font-semibold">{t('lulu_workbench.skills.section')}</h2>
      <p className="mb-npc-card mt-npc-gap-xs text-npc-caption text-npc-ink-medium">
        {t('lulu_workbench.skills.section_hint')}
      </p>
      {list.length === 0 ? (
        <p className="m-0 text-npc-caption text-npc-ink-soft">{t('lulu_workbench.skills.empty')}</p>
      ) : (
        <ul className="m-0 grid list-none gap-npc-gap-lg p-0 sm:grid-cols-2">
          {list.map((skill) => (
            <li key={skill.key} data-testid="lulu-skill-card" data-skill={skill.key} data-enabled={skill.enabled ? 'yes' : 'no'}>
              <Card className="flex h-full flex-col gap-npc-gap-sm">
                <div className="flex items-start justify-between gap-npc-gap-lg">
                  <CardTitle>{skill.name}</CardTitle>
                  <Toggle
                    checked={skill.enabled}
                    disabled={!canManage || toggle.isPending}
                    label={skill.enabled ? t('lulu_workbench.skills.enabled') : t('lulu_workbench.skills.disabled')}
                    id={`lulu-skill-toggle-${skill.key}`}
                    onCheckedChange={(next) => toggle.mutate({ key: skill.key, enabled: next })}
                  />
                </div>
                <CardHint>
                  {skill.trigger.daily && skill.trigger.at
                    ? t('lulu_workbench.skills.trigger_daily', { at: skill.trigger.at })
                    : null}
                  {skill.trigger.utterances.length > 0 ? (
                    <>
                      {skill.trigger.daily && skill.trigger.at ? ' · ' : ''}
                      {t('lulu_workbench.skills.trigger_say', { phrase: skill.trigger.utterances[0]! })}
                    </>
                  ) : null}
                </CardHint>
                <p className="m-0 text-npc-caption text-npc-ink-soft" data-testid="lulu-skill-last-run">
                  {skill.last_run
                    ? t('lulu_workbench.skills.last_run', {
                        when: agoText(skill.last_run.at, now),
                        state: t(
                          skill.last_run.state === 'succeeded'
                            ? 'lulu_workbench.skills.last_run_ok'
                            : 'lulu_workbench.skills.last_run_failed',
                        ),
                      })
                    : t('lulu_workbench.skills.never_ran')}
                </p>
                {failedKey === skill.key ? (
                  <p role="alert" className="m-0 text-npc-caption text-npc-red-ink">
                    {t('lulu_workbench.skills.toggle_failed')}
                  </p>
                ) : null}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
